import { Article } from '@components/article'
import { Breadcrumbs } from '@components/breadcrumbs'
import { Container } from '@components/layout/grid'
import { Text } from '@components/text'
import { webviewStore } from '@lib/store/ui/webview'
import { formatDate, formatTimezone } from '@lib/utils/date'
import { TEXT } from '@lib/utils/text'
import { LINK } from '@shared/util/link'
import { formatISO } from 'date-fns/formatISO'
import { parseISO } from 'date-fns/parseISO'
import { useRouter } from 'next/router'
import type { FunctionComponent, ReactNode } from 'react'

import styles from './policy.module.scss'

/**
 * Displays policy content.
 */
export const Policy: FunctionComponent<Props> = ({
    title = '',
    children,
    ...restOfProps
}) => {
    const [updated, effective] = [
        restOfProps.updated,
        restOfProps.effective,
    ].map((d) => (d ? formatTimezone(parseISO(d)) : undefined))

    const router = useRouter()

    return (
        <Container>
            <div className={styles.breadcrumbs}>
                <Breadcrumbs
                    current={title}
                    links={[
                        {
                            text: TEXT.policies.pagetitle,
                            href: LINK.policies(
                                webviewStore.isWebview,
                            ).serialize(router),
                        },
                    ]}
                />
            </div>
            <Article
                title={title}
                subtitle={
                    (updated || effective) && (
                        <Text tag='div' typeStyle='body-lg'>
                            {[
                                [updated, TEXT.lastUpdated] as const,
                                [effective, TEXT.effective] as const,
                            ].map(
                                ([value, text]) =>
                                    value && (
                                        <time
                                            className={styles.time}
                                            key={text}
                                            dateTime={formatISO(value, {
                                                representation: 'date',
                                            })}
                                        >
                                            {text}:&nbsp;
                                            {formatDate(value, 'MMMM d, yyyy')}
                                        </time>
                                    ),
                            )}
                        </Text>
                    )
                }
            >
                <div className={styles.body}>{children}</div>
            </Article>
        </Container>
    )
}

type Props = {
    title: string
    updated?: string
    effective?: string
    children?: ReactNode
    loading?: boolean
}
